/**
 * Каталог методов API для админ-страницы `/admin/api` (docs/05, docs/12).
 *
 * Единственный источник правды для страницы: она не парсит документацию, а
 * рендерит этот список. Тест `packages/server/test/api-catalog.test.ts`
 * сверяет каталог с реальными маршрутами Fastify, поэтому расхождение между
 * страницей и кодом ловится сборкой, а не глазами (docs/08 §5).
 *
 * Пути записаны в «документационной» форме `{key}` — как в docs/05 и docs/12;
 * тест приводит их к форме Fastify `:key` при сверке.
 */

import type { ApiScope } from './integration.js';

/** HTTP-методы, которые вообще встречаются в API. */
export type ApiHttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * Кто минимально нужен для вызова метода. Ровно один вариант на маршрут:
 * глобальный админ проходит любую проверку (docs/03 §5), поэтому
 * `project-admin` включает и его.
 */
export type ApiAccess =
  /** Только `role = admin` (управление пользователями, почтой, аудитом). */
  | { readonly kind: 'system-admin' }
  /** Админ проекта или глобальный админ (настройки конкретного проекта). */
  | { readonly kind: 'project-admin' }
  /** Любой аутентифицированный пользователь (списки для выбора исполнителя). */
  | { readonly kind: 'member' }
  /** Публичный API: только `Authorization: Bearer tasks_pat_…` (docs/12 §2). */
  | { readonly kind: 'token'; readonly scope?: ApiScope };

/** Группа методов: соответствует блоку аутентификации. */
export type ApiSurface = 'admin' | 'project' | 'public';

export interface ApiMethod {
  readonly method: ApiHttpMethod;
  readonly path: string;
  readonly purpose: string;
  readonly surface: ApiSurface;
  readonly access: ApiAccess;
}

/** Описание групп для заголовков страницы. */
export interface ApiSurfaceInfo {
  readonly id: ApiSurface;
  readonly title: string;
  readonly auth: string;
  readonly note: string;
}

export const API_SURFACES: readonly ApiSurfaceInfo[] = [
  {
    id: 'project',
    title: 'Настройки проекта',
    auth: 'Cookie-сессия + CSRF',
    note: 'Доступно администратору проекта и глобальному админу. Member получает 403 или 404.',
  },
  {
    id: 'admin',
    title: 'Администрирование системы',
    auth: 'Cookie-сессия + CSRF',
    note: 'Только глобальный админ (role = admin), кроме списков пользователей.',
  },
  {
    id: 'public',
    title: 'Публичный REST API',
    auth: 'Authorization: Bearer tasks_pat_…',
    note: 'Cookie и CSRF не применяются. Токен привязан к проекту и ограничен scope-ами.',
  },
];

/** Настройки проекта: workflow, участники, доски, интеграции (docs/05 §4–5). */
const PROJECT_METHODS: readonly ApiMethod[] = [
  {
    method: 'PATCH',
    path: '/api/projects/{key}',
    purpose: 'Изменить проект',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/archive',
    purpose: 'Архивировать проект',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/unarchive',
    purpose: 'Разархивировать проект',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}',
    purpose: 'Удалить проект (нужен ?confirm=KEY)',
    surface: 'project',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/members',
    purpose: 'Добавить участника',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/members/{userId}',
    purpose: 'Изменить роль участника',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/members/{userId}',
    purpose: 'Удалить участника',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/workflow',
    purpose: 'Статусы и переходы workflow по умолчанию',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/workflows',
    purpose: 'Список workflow с привязкой типов задач',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/workflows',
    purpose: 'Создать workflow',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/workflows/{workflowId}',
    purpose: 'Переименовать workflow',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/workflows/{workflowId}',
    purpose: 'Удалить workflow',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/workflows/{workflowId}/default',
    purpose: 'Назначить workflow по умолчанию',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PUT',
    path: '/api/projects/{key}/workflow/issue-types/{issueType}',
    purpose: 'Привязать тип задачи к workflow',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/statuses',
    purpose: 'Создать статус',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/statuses/{statusId}',
    purpose: 'Изменить статус',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/statuses/{statusId}',
    purpose: 'Удалить статус',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PUT',
    path: '/api/projects/{key}/statuses/order',
    purpose: 'Порядок статусов',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/transitions',
    purpose: 'Создать переход',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/transitions/{transitionId}',
    purpose: 'Изменить переход',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/transitions/{transitionId}',
    purpose: 'Удалить переход',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/boards',
    purpose: 'Создать доску',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/my/board',
    purpose: 'Персональная доска: мои задачи по всем проектам',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/boards/{boardId}',
    purpose: 'Изменить доску',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/boards/{boardId}',
    purpose: 'Удалить доску',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/boards/{boardId}/default',
    purpose: 'Сделать доской по умолчанию',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/boards/order',
    purpose: 'Порядок досок',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/components',
    purpose: 'Список компонентов проекта',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/components',
    purpose: 'Создать компонент',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/components/{componentId}',
    purpose: 'Изменить компонент',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/components/{componentId}/archive',
    purpose: 'Архивировать компонент',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/components/{componentId}/restore',
    purpose: 'Восстановить компонент из архива',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/components/{componentId}',
    purpose: 'Удалить неиспользуемый компонент',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/api-tokens',
    purpose: 'Список токенов проекта',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/api-tokens',
    purpose: 'Выпустить токен (секрет — один раз)',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/api-tokens/{id}',
    purpose: 'Отозвать токен',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/repository',
    purpose: 'Репозиторий проекта (без токена)',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PUT',
    path: '/api/projects/{key}/repository',
    purpose: 'Сохранить URL, ветку и read-only токен репозитория',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/repository/test',
    purpose: 'Проверить доступ к репозиторию проекта',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/repository/test-write',
    purpose: 'Проверить push-права и доступ к PR API',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/agents',
    purpose: 'Привязки AI-агентов проекта',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/agents',
    purpose: 'Добавить общего AI-агента в проект',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/agents/{agentId}',
    purpose: 'Изменить статусы привязки агента',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/agents/{agentId}',
    purpose: 'Отвязать агента и отозвать токен',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  // Подписок на вебхуки здесь нет: их UI не существует, администратор
  // заводит подписки внешней автоматизацией через /api/v1 (docs/12 §9).
];

/** Администрирование системы: пользователи, приглашения, почта, аудит. */
const ADMIN_METHODS: readonly ApiMethod[] = [
  {
    method: 'GET',
    path: '/api/admin/users',
    purpose: 'Список и поиск пользователей',
    surface: 'admin',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/admin/users/{id}',
    purpose: 'Карточка пользователя',
    surface: 'admin',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/admin/users',
    purpose: 'Создать пользователя',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/admin/users/{id}',
    purpose: 'Изменить пользователя',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/admin/agents/{id}',
    purpose: 'Удалить AI агента из Tasks',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/users/{id}/avatar',
    purpose: 'Загрузить аватар пользователя или агента',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/admin/users/{id}/avatar',
    purpose: 'Удалить аватар пользователя или агента',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/invitations',
    purpose: 'Список приглашений',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/invitations',
    purpose: 'Пригласить пользователя',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/invitations/{id}/resend',
    purpose: 'Повторно отправить приглашение',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/admin/invitations/{id}',
    purpose: 'Отозвать приглашение',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/settings/mail',
    purpose: 'Настройки почты',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'PUT',
    path: '/api/admin/settings/mail',
    purpose: 'Сохранить настройки почты',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/settings/mail/test',
    purpose: 'Тестовое письмо',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/agents',
    purpose: 'Список общих AI-агентов',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/shared',
    purpose: 'Создать общего AI-агента (аналитик или разработчик)',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/control-token',
    purpose: 'Перевыпустить control-токен общего агента',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/agents/{id}/setup',
    purpose: 'Настройки установки AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PATCH',
    path: '/api/admin/agents/{id}/setup',
    purpose: 'Сохранить настройки AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/server/test',
    purpose: 'Проверить SSH и ресурсы сервера',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/llm/test',
    purpose: 'Проверить LLM',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/agents/{id}/skills',
    purpose: 'Список своих навыков AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'PUT',
    path: '/api/admin/agents/{id}/skills/{filename}',
    purpose: 'Загрузить или заменить навык AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'DELETE',
    path: '/api/admin/agents/{id}/skills/{filename}',
    purpose: 'Удалить навык AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/install',
    purpose: 'Запустить установку AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/agents/{id}/install',
    purpose: 'Статус установки AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/install/{jobId}/cancel',
    purpose: 'Отменить установку',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/agents/{id}/install/{jobId}/log',
    purpose: 'Журнал установки',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/update',
    purpose: 'Обновить AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/apply',
    purpose: 'Применить настройки AI-агента',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'POST',
    path: '/api/admin/agents/{id}/uninstall',
    purpose: 'Удалить AI-агента с сервера',
    surface: 'admin',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/admin/audit',
    purpose: 'Журнал действий',
    surface: 'admin',
    access: { kind: 'system-admin' },
  },
];

/** Публичный REST API (docs/12 §4.1). */
const PUBLIC_METHODS: readonly ApiMethod[] = [
  {
    method: 'GET',
    path: '/api/v1/me',
    purpose: 'Информация о токене',
    surface: 'public',
    access: { kind: 'token' },
  },
  {
    method: 'GET',
    path: '/api/v1/agent/bindings',
    purpose: 'Привязки общего AI-агента (control-токен)',
    surface: 'public',
    access: { kind: 'token' },
  },
  {
    method: 'GET',
    path: '/api/v1/project',
    purpose: 'Проект токена',
    surface: 'public',
    access: { kind: 'token' },
  },
  {
    method: 'GET',
    path: '/api/v1/statuses',
    purpose: 'Статусы workflow',
    surface: 'public',
    access: { kind: 'token' },
  },
  {
    method: 'GET',
    path: '/api/v1/members',
    purpose: 'Участники проекта',
    surface: 'public',
    access: { kind: 'token' },
  },
  {
    method: 'GET',
    path: '/api/v1/components',
    purpose: 'Компоненты проекта',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:read' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues',
    purpose: 'Список и поиск задач',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/issues',
    purpose: 'Создать задачу',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues/{key}',
    purpose: 'Задача целиком',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:read' },
  },
  {
    method: 'PATCH',
    path: '/api/v1/issues/{key}',
    purpose: 'Изменить задачу (кроме статуса)',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:write' },
  },
  {
    method: 'DELETE',
    path: '/api/v1/issues/{key}',
    purpose: 'Удалить задачу (soft)',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues/{key}/transitions',
    purpose: 'Доступные переходы',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/issues/{key}/transitions',
    purpose: 'Выполнить переход статуса',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues/{key}/changelog',
    purpose: 'История изменений задачи',
    surface: 'public',
    access: { kind: 'token', scope: 'issues:read' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues/{key}/comments',
    purpose: 'Комментарии задачи',
    surface: 'public',
    access: { kind: 'token', scope: 'comments:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/issues/{key}/comments',
    purpose: 'Добавить комментарий',
    surface: 'public',
    access: { kind: 'token', scope: 'comments:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/issues/{key}/attachments',
    purpose: 'Вложения задачи',
    surface: 'public',
    access: { kind: 'token', scope: 'attachments:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/issues/{key}/attachments',
    purpose: 'Загрузить файл',
    surface: 'public',
    access: { kind: 'token', scope: 'attachments:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/attachments/{id}',
    purpose: 'Скачать файл',
    surface: 'public',
    access: { kind: 'token', scope: 'attachments:read' },
  },
  {
    method: 'GET',
    path: '/api/v1/webhooks',
    purpose: 'Список подписок',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/webhooks',
    purpose: 'Создать подписку',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/webhooks/{id}',
    purpose: 'Подписка',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:read' },
  },
  {
    method: 'PATCH',
    path: '/api/v1/webhooks/{id}',
    purpose: 'Изменить подписку',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'DELETE',
    path: '/api/v1/webhooks/{id}',
    purpose: 'Удалить подписку',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'POST',
    path: '/api/v1/webhooks/{id}/rotate-secret',
    purpose: 'Сменить секрет подписи',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'POST',
    path: '/api/v1/webhooks/{id}/test',
    purpose: 'Тестовая доставка',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/webhooks/{id}/deliveries',
    purpose: 'Журнал доставок',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/webhooks/{id}/deliveries/{deliveryId}/redeliver',
    purpose: 'Повторить доставку',
    surface: 'public',
    access: { kind: 'token', scope: 'webhooks:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/wiki/pages',
    purpose: 'Страницы вики с телами',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:read' },
  },
  {
    method: 'GET',
    path: '/api/v1/wiki/pages/{id}',
    purpose: 'Страница вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:read' },
  },
  {
    method: 'POST',
    path: '/api/v1/wiki/pages',
    purpose: 'Создать страницу вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:write' },
  },
  {
    method: 'PATCH',
    path: '/api/v1/wiki/pages/{id}',
    purpose: 'Изменить страницу вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:write' },
  },
  {
    method: 'DELETE',
    path: '/api/v1/wiki/pages/{id}',
    purpose: 'Удалить страницу вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:write' },
  },
  {
    method: 'GET',
    path: '/api/v1/wiki/pages/{id}/versions',
    purpose: 'История версий страницы вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:read' },
  },
  {
    method: 'GET',
    path: '/api/v1/wiki/search',
    purpose: 'Поиск по вики',
    surface: 'public',
    access: { kind: 'token', scope: 'wiki:read' },
  },
];

/** Полный каталог: проект → администрирование → публичный API. */
export const API_METHODS: readonly ApiMethod[] = [
  ...PROJECT_METHODS,
  ...ADMIN_METHODS,
  ...PUBLIC_METHODS,
  // --- Wiki проекта (docs/14 §6) ---
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages',
    purpose: 'Дерево страниц вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages/{pageId}',
    purpose: 'Страница вики с телом',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/pages',
    purpose: 'Создать страницу вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/wiki/pages/{pageId}',
    purpose: 'Изменить страницу вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/wiki/pages/{pageId}/move',
    purpose: 'Переместить страницу в дереве',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/wiki/pages/{pageId}',
    purpose: 'Удалить страницу вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages/{pageId}/versions',
    purpose: 'История версий страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages/{pageId}/diff',
    purpose: 'Разница двух версий страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/pages/{pageId}/revert',
    purpose: 'Восстановить версию страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/search',
    purpose: 'Поиск по страницам вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages/{pageId}/comments',
    purpose: 'Комментарии страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/pages/{pageId}/comments',
    purpose: 'Добавить комментарий страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/pages/{pageId}/attachments',
    purpose: 'Вложения страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/pages/{pageId}/attachments',
    purpose: 'Приложить файл к странице',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'GET',
    path: '/api/wiki/attachments/{attachmentId}',
    purpose: 'Скачать вложение страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'DELETE',
    path: '/api/wiki/attachments/{attachmentId}',
    purpose: 'Удалить вложение страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/import',
    purpose: 'Импорт markdown-архива в вики',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/export',
    purpose: 'Экспорт вики в markdown-архив',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/transfers',
    purpose: 'Журнал импорта и экспорта вики',
    surface: 'project',
    access: { kind: 'project-admin' },
  },
  {
    method: 'GET',
    path: '/api/projects/{key}/wiki/templates',
    purpose: 'Шаблоны страниц вики',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'POST',
    path: '/api/projects/{key}/wiki/templates',
    purpose: 'Создать шаблон страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'PATCH',
    path: '/api/projects/{key}/wiki/templates/{templateId}',
    purpose: 'Изменить шаблон страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
  {
    method: 'DELETE',
    path: '/api/projects/{key}/wiki/templates/{templateId}',
    purpose: 'Удалить шаблон страницы',
    surface: 'project',
    access: { kind: 'member' },
  },
];

/** Методы группы в порядке объявления. */
export function apiMethodsOf(surface: ApiSurface): readonly ApiMethod[] {
  return API_METHODS.filter((entry) => entry.surface === surface);
}
